import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import caseService from '../../services/caseService';
import { formatDate } from '../../utils/dateUtils';
import CaseOverviewTab from './CaseOverviewTab';
import CaseCommunicationTab from './CaseCommunicationTab';
import CaseDocumentsTab from './CaseDocumentsTab';
import CaseBillingTab from './CaseBillingTab';
import CaseTasks from './CaseTasks';
import CaseLitigationEvents from './CaseLitigationEvents';
import CourtRecordsPanel from './CourtRecordsPanel';

// Tab consolidation (2026-07-19): 22 tabs → 6. The 9 starving tabs (Courtroom,
// Case Brief, Hearing Prep, Submit, Alerts, Retention, Evidence, PlanchetteIntel,
// Intelligence — all backend-unmounted) are retired; Contact/Settings fold into
// Overview & Time, Updates into Communication, Discovery/DocIntel into Documents.
// Retired/merged components stay in the tree, just no longer tab-mounted. Any
// legacy activeTab id falls through the render switch to Overview (no blank tab).
const KNOWN_TABS = ['overview', 'communication', 'documents', 'litigation', 'time', 'tasks'];

const CaseDetail = ({ caseId, onBack }) => {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTabRaw] = useState('overview');
  const [needsResponse, setNeedsResponse] = useState(0);
  // Guard: unknown/legacy tab ids resolve to Overview so a dead deep-link never
  // renders a blank tab.
  const setActiveTab = (id) => setActiveTabRaw(KNOWN_TABS.includes(id) ? id : 'overview');
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState('soft'); // 'soft' or 'permanent'
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadCase();
  }, [caseId]);

  // needs-response count drives the Communication tab badge
  useEffect(() => {
    let cancelled = false;
    api.get(`/cases/${caseId}/feed`)
      .then((r) => { if (!cancelled) setNeedsResponse(r.data?.needs_response || 0); })
      .catch(() => { if (!cancelled) setNeedsResponse(0); });
    return () => { cancelled = true; };
  }, [caseId, activeTab]);

  const loadCase = async () => {
    try {
      setLoading(true);
      const data = await caseService.getCase(caseId);
      setCaseData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (type) => {
    setDeleteType(type);
    setShowDeleteMenu(false);
    setShowDeleteConfirm(true);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (deleteType === 'permanent' && deleteConfirmText !== 'DELETE') {
      return;
    }

    try {
      setDeleting(true);
      if (deleteType === 'soft') {
        await caseService.deleteCaseSoft(caseId);
      } else {
        await caseService.deleteCasePermanent(caseId);
      }
      
      // Show success message and go back
      alert(`Case ${deleteType === 'soft' ? 'deleted (can be restored)' : 'permanently deleted'}`);
      onBack();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete case');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📋' },
    { id: 'communication', name: 'Communication', icon: '📢', badge: needsResponse || undefined },
    { id: 'documents', name: 'Documents', icon: '📄', badge: caseData?.document_count },
    { id: 'litigation', name: 'Litigation', icon: '⚖️' },
    { id: 'time', name: 'Time & Billing', icon: '⏱️', badge: caseData?.time_summary?.entry_count },
    { id: 'tasks', name: 'Tasks', icon: '✓', badge: caseData?.task_count },
  ];

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/20 text-green-300 border border-green-500/30',
      pending: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
      closed: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
    };
    return colors[status] || 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 backdrop-blur-sm">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-4">
              {deleteType === 'soft' ? 'Delete Case?' : '⚠️ PERMANENTLY DELETE CASE?'}
            </h3>

            {deleteType === 'soft' ? (
              <div>
                <p className="text-virgo-teal-300 mb-4">
                  The case will be marked as deleted but can be restored later by an admin.
                </p>
                <p className="text-sm text-virgo-teal-400 mb-4">
                  Case: <strong className="text-white">{caseData.case_name}</strong> ({caseData.display_number})
                </p>
              </div>
            ) : (
              <div>
                <p className="text-red-400 font-semibold mb-2">
                  This action CANNOT be undone!
                </p>
                <p className="text-virgo-teal-300 mb-4">
                  This will permanently remove:
                </p>
                <ul className="text-sm text-virgo-teal-400 mb-4 list-disc list-inside">
                  <li>Case record</li>
                  <li>All case updates</li>
                  <li>All documents</li>
                  <li>All time entries</li>
                  <li>All tasks</li>
                  <li>All expenses</li>
                </ul>
                <p className="text-sm text-virgo-teal-300 mb-2">
                  Type <strong className="text-white">DELETE</strong> to confirm:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg px-3 py-2 mb-4 text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Type DELETE"
                  autoFocus
                />
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-virgo-teal-700/50 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-600 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting || (deleteType === 'permanent' && deleteConfirmText !== 'DELETE')}
                className={`flex-1 px-4 py-2 rounded-lg text-white disabled:opacity-50 transition-colors ${
                  deleteType === 'soft'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {deleting ? 'Deleting...' : deleteType === 'soft' ? 'Delete Case' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case Header */}
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-500/20 p-6 mb-6 backdrop-blur-sm">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-2xl font-bold text-white">{caseData.case_name}</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(caseData.status)}`}>
                {caseData.status}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
              <div className="bg-virgo-navy-800/50 rounded-lg p-3">
                <p className="text-virgo-teal-400">Case Number</p>
                <p className="font-semibold text-cyan-400">{caseData.display_number}</p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3">
                <p className="text-virgo-teal-400">Practice Area</p>
                <p className="font-semibold text-white">{caseData.practice_area || '—'}</p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3">
                <p className="text-virgo-teal-400">Responsible Attorney</p>
                <p className="font-semibold text-white">{caseData.responsible_attorney_name || caseData.responsible_attorney || '—'}</p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3">
                <p className="text-virgo-teal-400">Created</p>
                <p className="font-semibold text-white">
                  {formatDate(caseData.created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Delete Button */}
          <div className="relative ml-4">
            <button
              onClick={() => setShowDeleteMenu(!showDeleteMenu)}
              className="px-4 py-2 bg-red-600/80 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
            >
              Delete ▼
            </button>

            {showDeleteMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-virgo-navy-700 rounded-lg shadow-2xl border border-virgo-teal-700/30 z-10 overflow-hidden">
                <button
                  onClick={() => handleDeleteClick('soft')}
                  className="w-full text-left px-4 py-3 hover:bg-virgo-navy-600 border-b border-virgo-navy-600 transition-colors"
                >
                  <div className="font-medium text-white">Soft Delete</div>
                  <div className="text-xs text-virgo-teal-400">Can be restored later</div>
                </button>
                <button
                  onClick={() => handleDeleteClick('permanent')}
                  className="w-full text-left px-4 py-3 hover:bg-virgo-navy-600 transition-colors"
                >
                  <div className="font-medium text-red-400">Permanent Delete</div>
                  <div className="text-xs text-virgo-teal-400">Cannot be undone</div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 mb-6 backdrop-blur-sm">
        <div className="border-b border-virgo-navy-600">
          <nav className="flex space-x-6 px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-cyan-500 text-cyan-400'
                    : tab.highlight
                    ? 'border-transparent text-amber-400 hover:text-amber-300 hover:border-amber-500/50'
                    : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200 hover:border-virgo-teal-700'
                  }
                `}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
                {tab.highlight && activeTab !== tab.id && (
                  <span className="ml-1 w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                )}
                {tab.badge > 0 && (
                  <span className="ml-2 bg-cyan-500/30 text-cyan-300 py-0.5 px-2 rounded-full text-xs border border-cyan-500/30">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content — 6 consolidated tabs; unknown ids already coerced to overview */}
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 backdrop-blur-sm">
        {activeTab === 'communication' && <CaseCommunicationTab caseId={caseId} />}
        {activeTab === 'documents' && <CaseDocumentsTab caseId={caseId} caseName={caseData?.case_name} />}
        {activeTab === 'litigation' && (
          <>
            <CaseLitigationEvents caseId={caseId} caseName={caseData?.case_name} />
            <div className="px-6 pb-6">
              <CourtRecordsPanel caseId={caseId} />
            </div>
          </>
        )}
        {activeTab === 'time' && <CaseBillingTab caseId={caseId} onRefresh={loadCase} />}
        {activeTab === 'tasks' && <CaseTasks caseId={caseId} caseName={caseData?.case_name} />}
        {(activeTab === 'overview' || !KNOWN_TABS.includes(activeTab)) &&
          <CaseOverviewTab caseData={caseData} caseId={caseId} onRefresh={loadCase} />}
      </div>
    </div>
  );
};

export default CaseDetail;
